import { useLayoutEffect } from 'react';

/* v8 Path AB — collision-detection sticky cascade.
   Pure CSS cannot "push upward" sticky table rows when the next sticky
   row collides with the previous one (HTML tables disallow <div>
   wrappers between <tr>s, so each sticky shares one scope and stacks
   instead of superseding). This tiny scroll listener fixes that with a
   single CSS custom property:

     tr[data-sticky-row] {
       --sticky-h-top: 25px;
     }
     td.sticky {
       top: var(--sticky-h-top, 25px);
     }

   On every scroll frame we walk the list of sticky rows. For each row
   we compute where it would sit if fully pinned (containerTop + 25 +
   rowHeight). If the NEXT sticky row's current top is already above
   that line, they're colliding — we push the current row up by the
   overlap amount. Because top is read from the inherited CSS var,
   setting it on the <tr> cascades down to every sticky <td> in that
   row (no per-cell writes).

   Contract:
   - `containerRef` — ref to the scrollable wrapper (the max-h-[70vh] div)
   - `offsetPx`    — default sticky offset (thead height), 25 here

   Wrapped in requestAnimationFrame so scroll perf stays smooth. */
export function useStickyCascade(containerRef, offsetPx = 24) {
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof window === 'undefined') return;

    let rafId = null;
    const compute = () => {
      rafId = null;
      const contTop = container.getBoundingClientRect().top;
      const rows = container.querySelectorAll('tr[data-sticky-row]');
      for (let i = 0; i < rows.length; i++) {
        const cur = rows[i];
        const curRect = cur.getBoundingClientRect();
        const next = rows[i + 1];
        let newTop = offsetPx;
        if (next) {
          const nextRect = next.getBoundingClientRect();
          // Where the bottom of `cur` would be if it were pinned at offsetPx
          const curBottomIfSticky = contTop + offsetPx + curRect.height;
          if (nextRect.top < curBottomIfSticky) {
            // Collision: the next row is already approaching / overlapping.
            // Push `cur` up by exactly the overlap so its bottom kisses the
            // next row's top. Once overlap exceeds curRect.height, `cur`
            // disappears entirely — genuine "push upward" cascade.
            newTop = offsetPx - (curBottomIfSticky - nextRect.top);
          }
        }
        cur.style.setProperty('--sticky-h-top', `${newTop}px`);
      }
    };

    const schedule = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(compute);
    };

    container.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // Run once immediately so initial state is correct
    compute();

    return () => {
      container.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [containerRef, offsetPx]);
}
